import React, { useCallback, useEffect, useState } from 'react';
import { Backup } from '../types';
import { api } from '../services/apiService';
import { toast } from '../services/toast';

function fmtBytes(b: number | undefined): string {
  if (!b || b < 0) return '—';
  if (b < 1024) return `${b} o`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} Ko`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} Mo`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} Go`;
}

function fmtDate(iso: string): string {
  const d = new Date(iso.replace(' ', 'T'));
  return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const BackupsTab: React.FC = () => {
  const [backups, setBackups] = useState<Backup[]>([]);
  const [retention, setRetention] = useState<number>(10);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [busyFilename, setBusyFilename] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const data = await api.listBackups();
      setBackups(data.backups || []);
      setRetention(data.retention || 10);
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleCreate = async () => {
    if (!window.confirm(
      "Créer un backup complet ?\n\n" +
      "L'opération peut durer plusieurs secondes selon la taille de la BDD et des uploads. " +
      "Le CRM reste utilisable pendant la création."
    )) return;
    setCreating(true);
    try {
      const res = await api.createBackup();
      toast.success(`Backup créé : ${res.filename} (${fmtBytes(res.size_bytes)})`);
      await refresh();
    } catch (e: any) {
      toast.error(e?.message || 'Création du backup échouée');
    } finally {
      setCreating(false);
    }
  };

  const handleDownload = async (b: Backup) => {
    setBusyFilename(b.filename);
    try {
      await api.downloadBackup(b.filename);
      toast.success(`Téléchargement démarré : ${b.filename}`);
    } catch (e: any) {
      toast.error(e?.message || 'Téléchargement échoué');
    } finally {
      setBusyFilename(null);
    }
  };

  const handleDelete = async (b: Backup) => {
    if (!window.confirm(`Supprimer définitivement ce backup ?\n\n${b.filename}\n(${fmtBytes(b.size_bytes)})\n\nCette action est irréversible.`)) return;
    setBusyFilename(b.filename);
    try {
      await api.deleteBackup(b.filename);
      toast.success('Backup supprimé');
      await refresh();
    } catch (e: any) {
      toast.error(e?.message || 'Suppression échouée');
    } finally {
      setBusyFilename(null);
    }
  };

  const handleRestore = async (b: Backup) => {
    // Double confirmation
    if (!window.confirm(
      `⚠️ RESTAURATION ⚠️\n\n` +
      `Vous êtes sur le point de REMPLACER l'ensemble des données du CRM ` +
      `par le backup :\n\n${b.filename}\ndu ${fmtDate(b.created_at)}\n\n` +
      `Un backup de sécurité pré-restauration sera automatiquement créé.\n\n` +
      `Continuer ?`
    )) return;
    const confirmText = window.prompt(
      `Pour confirmer, tapez exactement : RESTAURER`
    );
    if (confirmText !== 'RESTAURER') {
      toast.error('Confirmation invalide — restauration annulée');
      return;
    }
    setBusyFilename(b.filename);
    try {
      const res = await api.restoreBackup(b.filename);
      toast.success(res.message);
      await refresh();
      // Optionnel : forcer un reload de l'app car les données ont changé
      setTimeout(() => window.location.reload(), 3000);
    } catch (e: any) {
      toast.error(e?.message || 'Restauration échouée');
    } finally {
      setBusyFilename(null);
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-20">
      {/* En-tête */}
      <div className="orion-admin-card p-8 rounded-2xl">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-2xl font-black text-[#e0eaff] mb-2">Sauvegardes</h2>
            <p className="text-[#7f93bc] text-sm max-w-2xl">
              Backup complet du CRM : base de données (mysqldump) + fichiers uploadés + manifest.
              Un fichier <code className="text-cyan-300 bg-[rgba(17,197,214,0.1)] px-1.5 py-0.5 rounded">.tar.gz</code> unique
              stocké sur le VPS, téléchargeable localement. Rétention automatique des {retention} derniers.
            </p>
          </div>
          <button
            type="button"
            onClick={handleCreate}
            disabled={creating}
            className="orion-primary-btn text-white px-5 py-3 rounded-xl text-xs font-black uppercase tracking-widest disabled:opacity-50"
          >
            {creating ? 'Création en cours…' : '+ Nouveau backup complet'}
          </button>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 rounded-xl bg-[rgba(17,197,214,0.06)] border border-[rgba(17,197,214,0.2)]">
            <div className="text-[10px] font-black uppercase tracking-widest text-[#546a96] mb-1">Backups disponibles</div>
            <div className="text-2xl font-black text-[#11c5d6]">{backups.length}</div>
          </div>
          <div className="p-4 rounded-xl bg-[rgba(100,140,210,0.08)] border border-[rgba(100,140,210,0.15)]">
            <div className="text-[10px] font-black uppercase tracking-widest text-[#546a96] mb-1">Rétention</div>
            <div className="text-2xl font-black text-[#e0eaff]">{retention}</div>
          </div>
          <div className="p-4 rounded-xl bg-[rgba(100,140,210,0.08)] border border-[rgba(100,140,210,0.15)]">
            <div className="text-[10px] font-black uppercase tracking-widest text-[#546a96] mb-1">Espace total</div>
            <div className="text-2xl font-black text-[#e0eaff]">
              {fmtBytes(backups.reduce((acc, b) => acc + (b.size_bytes || 0), 0))}
            </div>
          </div>
        </div>
      </div>

      {/* Liste */}
      <div className="orion-admin-card p-8 rounded-2xl">
        <h3 className="text-lg font-black text-[#e0eaff] mb-4">Liste des backups</h3>

        {error && (
          <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-xs text-rose-300 mb-4">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center text-xs text-[#546a96] py-8">Chargement…</div>
        ) : backups.length === 0 ? (
          <div className="text-center text-xs text-[#546a96] py-8">
            Aucun backup pour l'instant. Clique sur <strong className="text-[#11c5d6]">+ Nouveau backup complet</strong> pour en créer un.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] font-black uppercase tracking-widest text-[#546a96] border-b border-[rgba(100,140,210,0.15)]">
                  <th className="pb-3 pr-4">Fichier</th>
                  <th className="pb-3 pr-4">Date</th>
                  <th className="pb-3 pr-4">Taille</th>
                  <th className="pb-3 pr-4">Contenu</th>
                  <th className="pb-3 pr-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {backups.map(b => {
                  const isSafety = b.filename.includes('_pre_restore');
                  const busy = busyFilename === b.filename;
                  return (
                    <tr key={b.filename} className="border-b border-[rgba(100,140,210,0.08)] hover:bg-[rgba(100,140,210,0.04)]">
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-2">
                          <code className="text-[#e0eaff] font-mono text-[11px]">{b.filename}</code>
                          {isSafety && (
                            <span className="text-[9px] font-black uppercase tracking-wider text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5">
                              Auto pré-restauration
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 pr-4 text-[#c8daf5]">{fmtDate(b.created_at)}</td>
                      <td className="py-3 pr-4 text-[#c8daf5]">{fmtBytes(b.size_bytes)}</td>
                      <td className="py-3 pr-4">
                        {b.manifest ? (
                          <div className="text-[10px] text-[#7f93bc] leading-tight">
                            <div>{b.manifest.tables_count ?? '?'} tables</div>
                            {b.manifest.uploads_files !== undefined && (
                              <div>{b.manifest.uploads_files} fichier(s) uploadés</div>
                            )}
                          </div>
                        ) : (
                          <span className="text-[10px] text-[#546a96] italic">manifest indisponible</span>
                        )}
                      </td>
                      <td className="py-3 pr-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => handleDownload(b)}
                            disabled={busy}
                            title="Télécharger sur mon PC"
                            className="px-3 py-1.5 rounded-lg bg-[rgba(17,197,214,0.1)] hover:bg-[rgba(17,197,214,0.2)] text-cyan-300 text-[10px] font-black uppercase tracking-wider disabled:opacity-50"
                          >
                            Télécharger
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRestore(b)}
                            disabled={busy}
                            title="Restaurer ce backup — remplace toutes les données du CRM"
                            className="px-3 py-1.5 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 text-[10px] font-black uppercase tracking-wider disabled:opacity-50"
                          >
                            Restaurer
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(b)}
                            disabled={busy}
                            title="Supprimer définitivement"
                            className="p-2 rounded-lg text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 disabled:opacity-50"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Avertissement restauration */}
      <div className="orion-admin-card p-6 rounded-2xl border border-amber-500/30 bg-amber-500/5">
        <div className="flex items-start gap-3">
          <svg className="w-6 h-6 text-amber-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div>
            <h4 className="text-sm font-black text-amber-300 mb-2">Restauration — bonnes pratiques</h4>
            <ul className="text-xs text-[#c8daf5] space-y-1 leading-relaxed">
              <li>• Une restauration <strong>remplace TOUTES les données actuelles</strong> (BDD + fichiers uploadés).</li>
              <li>• Un <strong>safety-backup pré-restauration</strong> est créé automatiquement — utilisable pour rollback si erreur.</li>
              <li>• Downtime : quelques secondes à ~1 minute selon volumes. Prévenir les utilisateurs si nécessaire.</li>
              <li>• La page se recharge automatiquement 3s après la restauration réussie.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BackupsTab;
